import React, {
  ReactElement,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Container, Form, Modal, Row } from 'react-bootstrap';
import {
  EditableProperties,
  HeaderDisplayKeysWithType,
  OnRowRender,
  RowRenderResult,
} from './EditableTable';

type TValue = {
  [k: string]: unknown;
} & EditableProperties;

type SelectOn = {
  index?: number;
  checked: boolean;
};

type Props<T extends TValue> = {
  editable?: boolean;
  deleteable?: boolean;
  restorable?: boolean;
  nonScrollable?: boolean;
  headers: HeaderDisplayKeysWithType<T, keyof T>[];
  values: T[];
  fixCol?: number;
  onEdit?: (value: T) => void;
  onDelete?: (value: T) => void;
  onRestore?: (value: T) => void;
  showRestore?: (value: T) => boolean;
  onRowRender?: OnRowRender<T>;
  onSelected?: (selectOn: SelectOn) => void;
  context?: Record<string, unknown>;
};
// const tableStyle: CSSProperties = {
//   display: 'block',
//   overflowX: 'auto',
//   whiteSpace: 'nowrap',
// };

type RowDivProps<T extends TValue> = {
  value: T;
  rowIndex: number;
  headers: HeaderDisplayKeysWithType<T, keyof T>[];
  editable?: boolean;
  deleteable?: boolean;
  restorable?: boolean;
  fixCol?: number;
  onEdit?: (value: T) => void;
  onDelete?: (value: T) => void;
  onRestore?: (value: T) => void;
  showRestore?: (value: T) => boolean;
  onRowRender?: OnRowRender<T>;
  onSelected?: (selectOn: SelectOn) => void;
  context: Record<string, unknown>;
  fixLeftStartOffset: number;
  stickyOffsets: number[];
};

const rowDiv = <T extends TValue>({
  value: v,
  rowIndex: i,
  headers,
  editable,
  deleteable,
  restorable,
  fixCol,
  onEdit,
  onDelete,
  onRestore,
  showRestore,
  onRowRender,
  onSelected,
  context,
  fixLeftStartOffset,
  stickyOffsets,
}: RowDivProps<T>) => {
  const [deleting, setDeleting] = useState(false);

  const sticky: ReactElement[] = [];
  const normal: ReactElement[] = [];
  const canRestore = restorable && (showRestore ? showRestore(v) : true);

  headers.forEach((h, colIdx) => {
    const value = h.onRender
      ? h.onRender(v[h.property], i, false, { context, allValues: v })
      : v[h.property];
    if (fixCol && colIdx < fixCol) {
      sticky.push(
        <div
          className="div-table-cell div-sticky-left"
          style={{
            ...h.style,
            left: stickyOffsets[fixLeftStartOffset + colIdx],
          }}
          key={`body-${i}-${colIdx}`}
        >
          {value as ReactElement}
        </div>
      );
    } else {
      normal.push(
        <div
          className="div-table-cell"
          style={h.style}
          key={`body-${i}-${colIdx}`}
        >
          {value as ReactElement}
        </div>
      );
    }
  });

  const modifyCell: ReactElement[] = [];
  if (editable) {
    modifyCell.push(
      <Button
        key="cell-edit"
        className="border-0 px-2 py-1 m-0 mr-1"
        variant="outline-primary"
        size="sm"
        onClick={() => {
          if (onEdit) {
            onEdit(v);
          }
        }}
      >
        <span className="feather icon-edit" />
      </Button>
    );
  }
  if (deleteable) {
    modifyCell.push(
      <React.Fragment key="cell-delete-group">
        <Button
          className="border-0 px-2 py-1 m-0"
          variant="outline-danger"
          size="sm"
          onClick={() => {
            setDeleting(true);
          }}
        >
          <span className="feather icon-trash-2" />
        </Button>
        <Modal
          show={deleting}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          backdrop="static"
          centered
        >
          <Modal.Header>
            <Modal.Title id="contained-modal-title-vcenter">刪除</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>
              <Row className="mb-3">您確定要刪除嗎?</Row>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              variant="danger"
              onClick={() => {
                if (onDelete) {
                  onDelete(v);
                }
                setDeleting(false);
              }}
            >
              確認
            </Button>
            <Button
              type="reset"
              variant="secondary"
              onClick={() => {
                setDeleting(false);
              }}
            >
              取消
            </Button>
          </Modal.Footer>
        </Modal>
      </React.Fragment>
    );
  }
  if (canRestore) {
    modifyCell.push(
      <Button
        key="cell-restore"
        className="border-0 px-2 py-1 m-0"
        variant="outline-primary"
        size="sm"
        onClick={() => {
          if (onRestore) {
            onRestore(v);
          }
        }}
      >
        <span className="feather icon-refresh-cw" />
      </Button>
    );
  }

  if (editable || deleteable || restorable) {
    sticky.unshift(
      <div
        className="div-table-cell div-sticky-left"
        key="body-edit"
        style={{ left: stickyOffsets[onSelected ? 1 : 0] }}
      >
        {modifyCell}
      </div>
    );
  }

  if (onSelected) {
    sticky.unshift(
      <div
        className="div-table-cell div-sticky-left"
        key="body-select"
        style={{ left: stickyOffsets[0] }}
      >
        <Form.Check
          type="checkbox"
          checked={!!v.selected}
          onChange={() => {
            onSelected({ index: i, checked: !v.selected });
          }}
        />
      </div>
    );
  }

  const { rowStyle, rowClassName } = onRowRender
    ? onRowRender(v)
    : ({} as RowRenderResult);
  const fullClassName = ['div-table-row', rowClassName]
    .filter((s) => s)
    .join(' ');

  return (
    <div className={fullClassName} style={rowStyle}>
      {sticky}
      {normal}
    </div>
  );
};

const RowDiv = rowDiv as <T extends TValue>(
  props: RowDivProps<T>
) => ReactElement;

const editableTableDiv = <T extends TValue>({
  headers,
  values,
  editable,
  deleteable,
  restorable,
  nonScrollable,
  fixCol,
  onEdit,
  onDelete,
  onRestore,
  showRestore,
  onRowRender,
  onSelected,
  context = {},
}: Props<T>) => {
  const refs: RefObject<HTMLDivElement>[] = Array.from({
    length:
      +!!onSelected +
      +(editable || deleteable || restorable || false) +
      (fixCol || 0),
  }).map(() => useRef<HTMLDivElement>(null));
  const refLefts = useMemo(
    () => refs.map((r) => r.current?.offsetLeft),
    refs.map((r) => r.current?.offsetLeft)
  );
  const fixLeftStartOffset =
    +!!onSelected + +(editable || deleteable || restorable || false);

  const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);

  const headerElements = headers.reduce(
    (result, h, i) => {
      if (fixCol && i < fixCol) {
        result.sticky.push(
          <div
            ref={refs[i + fixLeftStartOffset]}
            className="div-table-cell div-sticky-left"
            style={h.style}
            key={`header-${i}`}
          >
            {h.display}
          </div>
        );
      } else {
        result.normal.push(
          <div className="div-table-cell" style={h.style} key={`header-${i}`}>
            {h.display}
          </div>
        );
      }

      return result;
    },
    { sticky: [] as ReactElement[], normal: [] as ReactElement[] }
  );

  if (editable || deleteable || restorable) {
    headerElements.sticky.unshift(
      <div
        ref={refs[onSelected ? 1 : 0]}
        className="div-table-cell div-sticky-left"
        style={{
          width: (+!!editable + +!!deleteable + +!!restorable) * 2.5 + 'rem',
        }}
        key={`header-edit`}
      ></div>
    );
  }

  if (onSelected) {
    const target = fixCol ? headerElements.sticky : headerElements.normal;
    target.unshift(
      <div
        className="div-table-cell div-sticky-left"
        key={`header-${target.length}-select`}
        style={{ width: '2rem' }}
        ref={refs[0]}
      >
        <Form.Check
          type="checkbox"
          checked={values.every((v) => v.selected)}
          onChange={(e) => {
            const checked = e.target.checked;
            onSelected({ checked });
          }}
        />
      </div>
    );
  }

  useEffect(() => {
    const offsets: number[] = [];
    let offset = 0;
    refs.forEach((s) => {
      s.current?.style.setProperty('left', `${offset}px`);
      offsets.push(offset);
      offset += s.current?.offsetWidth || 0;
    });
    setStickyOffsets(offsets);
  }, [values, ...refs.map((r) => r.current?.offsetWidth)]);

  return (
    <>
      <div className="div-table-responsive">
        <div
          className={
            'table table-bordered table-striped table-hover div-table text-center ' +
            (nonScrollable ? '' : 'div-table-max-content')
          }
        >
          <div className="div-table-row div-sticky-top div-table-header">
            {headerElements.sticky}
            {headerElements.normal}
          </div>
          {values.map((v, i) => (
            <RowDiv
              key={`row-${i}`}
              value={v}
              rowIndex={i}
              headers={headers}
              editable={editable}
              deleteable={deleteable}
              restorable={restorable}
              showRestore={showRestore}
              fixCol={fixCol}
              onEdit={onEdit}
              onDelete={onDelete}
              onRestore={onRestore}
              onRowRender={onRowRender}
              onSelected={onSelected}
              context={context}
              fixLeftStartOffset={fixLeftStartOffset}
              stickyOffsets={stickyOffsets}
            />
          ))}
        </div>
        {values.length === 0 ? (
          <div className="w-100 p-2 text-center">目前資料表中沒有資料</div>
        ) : null}
      </div>
    </>
  );
};

export const EditableTableDiv = editableTableDiv;
